/**
 * The authoritative movement simulation.
 *
 * ============================================================================
 * THIS FILE RUNS UNMODIFIED ON BOTH THE CLIENT AND THE SERVER.
 * ============================================================================
 *
 * The client predicts the local player by calling `step` immediately on its own
 * input; the server calls the same `step` on the same input a fraction of a
 * second later. If the two ever compute different results, the player
 * rubber-bands — and that class of bug is agonising to attribute after the
 * fact. So:
 *
 *   - No `Date.now()`, `Math.random()`, or any other ambient state.
 *   - No dependence on object iteration order or on how many solids the
 *     broadphase happened to return (collision resolution below is written to
 *     be order-independent, not merely order-stable).
 *   - No reading of anything mutable that is not passed in as an argument.
 *
 * On exact determinism: we deliberately do NOT chase bit-exactness across
 * JS engines. `Math.sin`/`Math.cos` are not specified to the last ULP, so a
 * Firefox client talking to a V8 server can differ in the final bit. That is
 * fine, because reconciliation is epsilon-tolerant (RECONCILE_EPSILON) rather
 * than exact-match. What the determinism test in movement.test.ts actually
 * protects against is *accidental* nondeterminism — ambient state, ordering
 * dependence, shared mutable scratch leaking between callers. That is the bug
 * class that actually bites.
 *
 * The client is responsible for quantizing yaw/pitch through the wire codec
 * BEFORE feeding an input frame to `step`. Otherwise the client predicts using
 * full float precision while the server replays the rounded value it received,
 * and every single tick carries a small guaranteed mismatch.
 */

import {
  AIR_ACCEL,
  COLLIDE_EPSILON,
  CROUCH_EYE_HEIGHT,
  CROUCH_HEIGHT,
  CROUCH_SPEED_SCALE,
  GRAVITY,
  GROUND_ACCEL,
  GROUND_FRICTION,
  JUMP_VELOCITY,
  MAX_SUBSTEP_DISTANCE,
  MAX_SUBSTEPS,
  PLAYER_EYE_HEIGHT,
  PLAYER_HALF_WIDTH,
  PLAYER_HEIGHT,
  STEP_HEIGHT,
  TERMINAL_VELOCITY,
} from "./rules.js";
import type { StaticWorld } from "./world.js";

/** Input button bitfield. Fits in one byte on the wire. */
export const Btn = {
  Forward: 1 << 0,
  Back: 1 << 1,
  Left: 1 << 2,
  Right: 1 << 3,
  Jump: 1 << 4,
  Crouch: 1 << 5,
  Fire: 1 << 6,
  Use: 1 << 7,
} as const;

export interface InputFrame {
  /** Monotonic per-client sequence number. Wraps at 2^16 on the wire. */
  seq: number;
  buttons: number;
  /** Quantized before use — see the file header. */
  yaw: number;
  pitch: number;
}

/**
 * A collision hull. Always an axis-aligned box anchored at the feet, so `y` is
 * the bottom of the hull rather than the centre — which makes ground contact
 * and prop placement (props sit on the floor) trivial to reason about.
 */
export interface Hull {
  halfWidth: number;
  halfDepth: number;
  height: number;
}

export interface MoveConfig {
  hull: Hull;
  maxSpeed: number;
}

export interface MoveState {
  x: number;
  y: number;
  z: number;
  vx: number;
  vy: number;
  vz: number;
  yaw: number;
  pitch: number;
  onGround: boolean;
}

export const DEFAULT_HULL: Hull = {
  halfWidth: PLAYER_HALF_WIDTH,
  halfDepth: PLAYER_HALF_WIDTH,
  height: PLAYER_HEIGHT,
};

export function createMoveState(x = 0, y = 0, z = 0): MoveState {
  return { x, y, z, vx: 0, vy: 0, vz: 0, yaw: 0, pitch: 0, onGround: false };
}

export function copyMoveState(src: MoveState, dst: MoveState = createMoveState()): MoveState {
  dst.x = src.x; dst.y = src.y; dst.z = src.z;
  dst.vx = src.vx; dst.vy = src.vy; dst.vz = src.vz;
  dst.yaw = src.yaw; dst.pitch = src.pitch;
  dst.onGround = src.onGround;
  return dst;
}

export function positionError(a: MoveState, b: MoveState): number {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  const dz = a.z - b.z;
  return Math.sqrt(dx * dx + dy * dy + dz * dz);
}

export function hullHeightFor(base: Hull, crouching: boolean): number {
  return crouching ? Math.min(CROUCH_HEIGHT, base.height) : base.height;
}

/**
 * Height of the eye above the feet.
 *
 * Shared rather than a renderer detail because a shot originates here too, and
 * the camera and the ray must agree or a crouched hunter's shots leave from
 * somewhere other than where the crosshair is pointing. `CROUCH_EYE_HEIGHT`
 * existed unused until tagging needed exactly this.
 *
 * Unlike `hullHeightFor` this does not clamp against the hull: a possessed
 * hider inside a 0.6 m crate still looks out from a person's eye line, because
 * the camera is the player's and the crate is a costume. Only the *hull*
 * changes shape.
 */
export function eyeHeightFor(crouching: boolean): number {
  return crouching ? CROUCH_EYE_HEIGHT : PLAYER_EYE_HEIGHT;
}

/**
 * Advance one simulation tick. Mutates `s` in place.
 *
 * `scratch` is passed in rather than held at module scope so that concurrent
 * callers (the server steps 16 players per tick; the client replays a whole
 * input buffer inside one frame) cannot alias each other's working state. A
 * module-level scratch array is exactly the kind of hidden shared mutable
 * state that turns into an unreproducible desync.
 */
export function step(
  s: MoveState,
  input: InputFrame,
  cfg: MoveConfig,
  world: StaticWorld,
  dt: number,
  scratch: number[] = [],
): void {
  s.yaw = input.yaw;
  s.pitch = input.pitch;

  const crouching = (input.buttons & Btn.Crouch) !== 0;
  const height = hullHeightFor(cfg.hull, crouching);
  const maxSpeed = cfg.maxSpeed * (crouching ? CROUCH_SPEED_SCALE : 1);

  // --- Wish direction -------------------------------------------------------
  // yaw 0 faces -Z, matching Three.js's default camera orientation.
  let forward = 0;
  let strafe = 0;
  if (input.buttons & Btn.Forward) forward += 1;
  if (input.buttons & Btn.Back) forward -= 1;
  if (input.buttons & Btn.Right) strafe += 1;
  if (input.buttons & Btn.Left) strafe -= 1;

  const sy = Math.sin(s.yaw);
  const cy = Math.cos(s.yaw);
  let wishX = -sy * forward + cy * strafe;
  let wishZ = -cy * forward - sy * strafe;

  const wishLen = Math.sqrt(wishX * wishX + wishZ * wishZ);
  if (wishLen > 0) {
    // Normalize so that holding two keys does not travel faster diagonally.
    wishX /= wishLen;
    wishZ /= wishLen;
  }

  // --- Friction -------------------------------------------------------------
  if (s.onGround) {
    const speed = Math.sqrt(s.vx * s.vx + s.vz * s.vz);
    if (speed > 0) {
      const drop = GROUND_FRICTION * dt;
      const scale = speed > drop ? (speed - drop) / speed : 0;
      s.vx *= scale;
      s.vz *= scale;
    }
  }

  // --- Acceleration ---------------------------------------------------------
  if (wishLen > 0) {
    const accel = s.onGround ? GROUND_ACCEL : AIR_ACCEL;
    // Only accelerate up to maxSpeed *along the wish direction*. Speed already
    // carried in other directions is preserved, which is what makes air
    // movement feel like an FPS rather than a car.
    const speedAlongWish = s.vx * wishX + s.vz * wishZ;
    const addSpeed = maxSpeed - speedAlongWish;
    if (addSpeed > 0) {
      const accelSpeed = Math.min(accel * dt, addSpeed);
      s.vx += wishX * accelSpeed;
      s.vz += wishZ * accelSpeed;
    }
  }

  // --- Jump -----------------------------------------------------------------
  if (s.onGround && (input.buttons & Btn.Jump) !== 0) {
    s.vy = JUMP_VELOCITY;
    s.onGround = false;
  }

  // --- Gravity --------------------------------------------------------------
  s.vy -= GRAVITY * dt;
  if (s.vy < -TERMINAL_VELOCITY) s.vy = -TERMINAL_VELOCITY;

  // --- Integrate with collision --------------------------------------------
  // Axis-separated: this is what produces wall sliding, and it keeps each
  // resolution step to a single scalar comparison.
  //
  // Sub-stepped because resolution tests only the destination box. A hull that
  // travels further in one tick than a solid is thick passes straight through
  // it — reachable at terminal velocity (3 m/tick) against the 1 m floor slab,
  // not just a theoretical concern. Ordinary running costs one sub-step.
  //
  // Ground contact is read before it is cleared: stepping up is something a
  // walking player does, and something an airborne one must never do, or the
  // retry below becomes a wall climb. The jump above has already cleared the
  // flag, so the tick you jump is not a tick you can step.
  const wasOnGround = s.onGround;
  s.onGround = false;
  const maxAxisTravel = Math.max(Math.abs(s.vx), Math.abs(s.vy), Math.abs(s.vz)) * dt;
  const substeps =
    maxAxisTravel > MAX_SUBSTEP_DISTANCE
      ? Math.min(Math.ceil(maxAxisTravel / MAX_SUBSTEP_DISTANCE), MAX_SUBSTEPS)
      : 1;
  const subDt = dt / substeps;

  for (let i = 0; i < substeps; i++) {
    // Read velocity fresh each sub-step: a sub-step that hits something zeroes
    // that axis, and the remaining sub-steps must respect that.
    moveHorizontal(s, cfg.hull, height, world, s.vx * subDt, s.vz * subDt, wasOnGround, scratch);
    moveAxisY(s, cfg.hull, height, world, s.vy * subDt, scratch);
  }
}

/**
 * The horizontal half of a sub-step, with one retry from a step up.
 *
 * Collision is axis-aligned boxes and nothing else — there is no slope
 * primitive and no ground normal — so every change in floor level is a
 * vertical face that `moveAxisX`/`moveAxisZ` stop you dead against. A level
 * with terrain in it needs a player who can walk up a stair, and this is that:
 * if the flat move was obstructed, try the identical move again from
 * `STEP_HEIGHT` higher, and keep it only if it got further.
 *
 * Three things keep it from being a climbing tool:
 *
 *  - `canStep` is ground contact from the start of the tick, so nothing is
 *    climbable in mid-air and the tick you jump is not a tick you can step.
 *  - The rise itself is a real collision move, so a hull with less than
 *    `STEP_HEIGHT` of headroom cannot step at all.
 *  - The raised move must make *more progress* than the flat one. A wall that
 *    is still there half a metre up blocks both identically, so nothing
 *    happens and the flat result stands.
 *
 * Both axes are retried together rather than one at a time: a step taken into
 * a corner would otherwise be taken twice, once per axis, and lift the player
 * a metre.
 *
 * Determinism is unaffected. Every move below is one of the order-independent
 * axis resolutions described under `moveAxisX`, the sequence of them is fixed,
 * and nothing is read that was not passed in.
 */
function moveHorizontal(
  s: MoveState, hull: Hull, height: number, world: StaticWorld,
  dx: number, dz: number, canStep: boolean, scratch: number[],
): void {
  const startX = s.x;
  const startZ = s.z;
  const startY = s.y;
  const startVx = s.vx;
  const startVz = s.vz;
  const startVy = s.vy;

  const hitX = moveAxisX(s, hull, height, world, dx, scratch);
  const hitZ = moveAxisZ(s, hull, height, world, dz, scratch);
  if (!canStep || (!hitX && !hitZ)) return;

  // What stopping flat achieved, and what the retry has to beat. Compared as
  // squared distance travelled, so a step is only taken when it buys ground on
  // the plane rather than on one axis at the other's expense.
  const flatX = s.x;
  const flatZ = s.z;
  const flatVx = s.vx;
  const flatVz = s.vz;
  const flatGain = (flatX - startX) ** 2 + (flatZ - startZ) ** 2;

  s.x = startX;
  s.z = startZ;
  s.vx = startVx;
  s.vz = startVz;

  // Rise, move, settle. `moveAxisY` is reused for the rise rather than a
  // bespoke overlap test so that "is there room to stand a step higher" is
  // answered by the same code that answers it everywhere else; a hit here is a
  // ceiling, and a player who cannot stand up cannot step up.
  const blockedAbove = moveAxisY(s, hull, height, world, STEP_HEIGHT, scratch);
  if (!blockedAbove) {
    moveAxisX(s, hull, height, world, dx, scratch);
    moveAxisZ(s, hull, height, world, dz, scratch);
    if ((s.x - startX) ** 2 + (s.z - startZ) ** 2 > flatGain) {
      // Back down onto whatever we stepped onto. Landing sets `onGround`, which
      // is what makes the top of a stair walkable rather than a place you
      // arrive at falling. Finding nothing there is not a failure: it means we
      // stepped over something low with a drop behind it, and gravity takes it
      // from here.
      moveAxisY(s, hull, height, world, -STEP_HEIGHT, scratch);
      return;
    }
  }

  s.x = flatX;
  s.z = flatZ;
  s.y = startY;
  s.vx = flatVx;
  s.vz = flatVz;
  s.vy = startVy;
}

/**
 * Resolution strategy, shared by all three axes:
 *
 * apply the whole displacement, then find the single most restrictive limit
 * across *every* overlapping solid, testing each against the same post-move
 * bounds. Because no solid's test depends on a previous solid's resolution,
 * the result does not depend on the order the broadphase returned them. That
 * is a stronger guarantee than sorting the indices, and it is the property
 * that lets client and server agree without coordinating.
 *
 * Returns whether the move was obstructed, which is what `moveHorizontal`
 * needs in order to know a step up is worth attempting.
 */
function moveAxisX(
  s: MoveState, hull: Hull, height: number,
  world: StaticWorld, dx: number, scratch: number[],
): boolean {
  if (dx === 0) return false;
  s.x += dx;

  const minX = s.x - hull.halfWidth;
  const maxX = s.x + hull.halfWidth;
  const minY = s.y;
  const maxY = s.y + height;
  const minZ = s.z - hull.halfDepth;
  const maxZ = s.z + hull.halfDepth;

  world.query(minX, minZ, maxX, maxZ, scratch);
  let limit = dx > 0 ? Infinity : -Infinity;
  let hit = false;

  for (let i = 0; i < scratch.length; i++) {
    const b = world.solids[scratch[i]!]!;
    if (minX >= b.maxX || maxX <= b.minX) continue;
    if (minY >= b.maxY || maxY <= b.minY) continue;
    if (minZ >= b.maxZ || maxZ <= b.minZ) continue;
    hit = true;
    if (dx > 0) {
      const l = b.minX - hull.halfWidth - COLLIDE_EPSILON;
      if (l < limit) limit = l;
    } else {
      const l = b.maxX + hull.halfWidth + COLLIDE_EPSILON;
      if (l > limit) limit = l;
    }
  }

  if (hit) {
    s.x = limit;
    s.vx = 0;
  }
  return hit;
}

function moveAxisZ(
  s: MoveState, hull: Hull, height: number,
  world: StaticWorld, dz: number, scratch: number[],
): boolean {
  if (dz === 0) return false;
  s.z += dz;

  const minX = s.x - hull.halfWidth;
  const maxX = s.x + hull.halfWidth;
  const minY = s.y;
  const maxY = s.y + height;
  const minZ = s.z - hull.halfDepth;
  const maxZ = s.z + hull.halfDepth;

  world.query(minX, minZ, maxX, maxZ, scratch);
  let limit = dz > 0 ? Infinity : -Infinity;
  let hit = false;

  for (let i = 0; i < scratch.length; i++) {
    const b = world.solids[scratch[i]!]!;
    if (minX >= b.maxX || maxX <= b.minX) continue;
    if (minY >= b.maxY || maxY <= b.minY) continue;
    if (minZ >= b.maxZ || maxZ <= b.minZ) continue;
    hit = true;
    if (dz > 0) {
      const l = b.minZ - hull.halfDepth - COLLIDE_EPSILON;
      if (l < limit) limit = l;
    } else {
      const l = b.maxZ + hull.halfDepth + COLLIDE_EPSILON;
      if (l > limit) limit = l;
    }
  }

  if (hit) {
    s.z = limit;
    s.vz = 0;
  }
  return hit;
}

function moveAxisY(
  s: MoveState, hull: Hull, height: number,
  world: StaticWorld, dy: number, scratch: number[],
): boolean {
  if (dy === 0) return false;
  s.y += dy;

  const minX = s.x - hull.halfWidth;
  const maxX = s.x + hull.halfWidth;
  const minY = s.y;
  const maxY = s.y + height;
  const minZ = s.z - hull.halfDepth;
  const maxZ = s.z + hull.halfDepth;

  world.query(minX, minZ, maxX, maxZ, scratch);
  let limit = dy > 0 ? Infinity : -Infinity;
  let hit = false;

  for (let i = 0; i < scratch.length; i++) {
    const b = world.solids[scratch[i]!]!;
    if (minX >= b.maxX || maxX <= b.minX) continue;
    if (minY >= b.maxY || maxY <= b.minY) continue;
    if (minZ >= b.maxZ || maxZ <= b.minZ) continue;
    hit = true;
    if (dy > 0) {
      const l = b.minY - height - COLLIDE_EPSILON;
      if (l < limit) limit = l;
    } else {
      const l = b.maxY + COLLIDE_EPSILON;
      if (l > limit) limit = l;
    }
  }

  if (hit) {
    s.y = limit;
    s.vy = 0;
    // Landing, not head-bonking.
    if (dy < 0) s.onGround = true;
  }
  return hit;
}

/**
 * Would a hull at this position intersect the static world?
 *
 * Used for spawn placement now, and in M2 to reject a prop swap whose new hull
 * does not fit where the player is standing — without that check, swapping to
 * a larger prop inside a wall is a free noclip.
 */
export function hullFits(
  world: StaticWorld,
  hull: Hull,
  x: number, y: number, z: number,
  scratch: number[] = [],
): boolean {
  const minX = x - hull.halfWidth;
  const maxX = x + hull.halfWidth;
  const minY = y;
  const maxY = y + hull.height;
  const minZ = z - hull.halfDepth;
  const maxZ = z + hull.halfDepth;

  world.query(minX, minZ, maxX, maxZ, scratch);
  for (let i = 0; i < scratch.length; i++) {
    const b = world.solids[scratch[i]!]!;
    if (minX >= b.maxX || maxX <= b.minX) continue;
    if (minY >= b.maxY || maxY <= b.minY) continue;
    if (minZ >= b.maxZ || maxZ <= b.minZ) continue;
    return false;
  }
  return true;
}
